import {
  formatPhoneNumber,
  LabelledItemList,
  LabelledItemShape,
  renderCountryFormattedAddress,
  renderFormattedAddress,
  renderShortFormattedAddress,
  renderStateFormattedAddress,
} from '@rabbit/elements/shared-components';
import {
  Address,
  DTConsumer_Private,
  DTHoldingProxy,
  SELF_REGISTERED_VENDABLE,
} from '@rabbit/data/types';
import {
  calculateTotalSpent,
  getMostRecentPurchaseDate,
} from '@rabbit/sage/utils/helpers';
import { useTranslation } from 'react-i18next';
import {
  getFirstName,
  getLastName,
} from 'libs/bizproc/react/src/shared/helpers';
import { useContext } from 'react';
import { AppContext } from '@rabbit/app-context';
import { Capitalise } from '../../../../../../libs/data/debug/src/generators/elements.ts';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface CustomerDetailsContentSectionProps {
  consumer_private: DTConsumer_Private;
  holdingsList: DTHoldingProxy[];
  favouriteStore?: string;
}

export function CustomerDetailsContentSection(
  props: CustomerDetailsContentSectionProps
) {
  const { t } = useTranslation();
  const { consumer_private, holdingsList, favouriteStore } = props || {};
  const { config, tenantInfo } = useContext(AppContext);

  const firstName =
    consumer_private.splitname?.first ??
    (consumer_private.fullname && consumer_private.fullname.indexOf('@') === -1)
      ? getFirstName(consumer_private.fullname)
      : '';

  const lastName =
    consumer_private.splitname?.last ??
    (consumer_private.fullname && consumer_private.fullname.indexOf('@') === -1)
      ? getLastName(consumer_private.fullname)
      : '';

  //if (!consumer_private || isLoading) return <LoadingSpinner size="xs" />;

  const mostRecentPurchaseDate = getMostRecentPurchaseDate(holdingsList);
  const carTenants =
    t('tenantLink') === 'WARRANTYIRELAND' ||
    t('tenantLink') === 'PINNACLEWARRANTIES' ||
    t('tenantLink') === 'NUCOVER';

  const totalSpent = carTenants ? 0 : calculateTotalSpent(holdingsList);
  const preferredMethodOfCommunication = !consumer_private
    ?.preferred_contact?.[0]
    ? '-'
    : consumer_private?.preferred_contact.join(', ');

  const numberOfPurchases = holdingsList
    ?.map((holding: DTHoldingProxy) => holding?.vendable)
    .filter((holding) => holding !== SELF_REGISTERED_VENDABLE).length;

  const items: LabelledItemShape[] = [
    {
      label: t('general.firstName'),
      value: `${firstName}`,
    },
    {
      label: t('general.lastName'),
      value: `${lastName}`,
    },
    {
      label: t('general.email'),
      value: consumer_private?.email,
    },
  ];
  let items2: LabelledItemShape[] = [
    ...(config.HOLDINGS.CONSUMER.TYPE_OF_CONSUMER
      ? [
          {
            label: t('general.customerType'),
            value: `${Capitalise(consumer_private.type_of_customer ?? '-')}`,
          },
        ]
      : []),
    {
      label: t('general.phoneNumber'),
      value: formatPhoneNumber(consumer_private?.phone),
    },
    {
      label: t('general.address'),
      value: renderShortFormattedAddress(
        (consumer_private?.address as Address[]).find((i) => i.isDefault) ||
          (consumer_private?.address || [])[0]
      ),
    },
    {
      label: t('general.cityAndState'),
      value: renderStateFormattedAddress(
        (consumer_private?.address as Address[]).find((i) => i.isDefault) ||
          (consumer_private?.address || [])[0]
      ),
    },
    {
      label: t('general.country'),
      value: renderCountryFormattedAddress(
        (consumer_private?.address as Address[]).find((i) => i.isDefault) ||
          (consumer_private?.address || [])[0]
      ),
    },
  ];
  let items3: LabelledItemShape[] = [];
  if (!carTenants) {
    items3 = [
      {
        label: t('general.numberOfPurchases'),
        value: `${numberOfPurchases}`,
      },
      {
        label: t('general.favouriteStore'),
        value: `${favouriteStore || '-'}`,
      },
      {
        label: t('general.mostRecentPurchaseDate'),
        value: `${mostRecentPurchaseDate}`,
      },
      {
        label: t('general.preferredMethodOfCommunication'),
        value: `${preferredMethodOfCommunication}`,
      },
      {
        label: t('general.totalSpent'),
        value: totalSpent,
      },
      ...(config.CUSTOMER.SHOW_COMMERCIAL_INFO
        ? [
            {
              label: t('general.commercialUser'),
              value: `${consumer_private?.isCommercialUser ? 'Yes' : 'No'}`,
            },
          ]
        : []),
    ];
  }

  return (
    <>
      <div className="font-nunito">
        <div className="text-md mb-5  font-light">
          {t('general.primaryContactDetails')}
        </div>
        {consumer_private && (
          <LabelledItemList
            items={items}
            listWrapperClasses={
              'font-nunito grid grid-cols-2 md:grid-cols-3 gap-y-6 gap-x-4'
            }
          />
        )}
      </div>
      <div className="font-nunito mt-10">
        {consumer_private && <LabelledItemList items={items2} />}
      </div>
      <div className="font-nunito mt-10">
        {consumer_private && <LabelledItemList items={items3} />}
      </div>
    </>
  );
}

export default CustomerDetailsContentSection;
